import React, {
    ReactElement, useEffect, useState,
} from "react";
import { AiOutlineEdit } from "react-icons/ai";
import { BsTrash } from "react-icons/bs";
import { Loading } from "../../../../components";
import Avatar from "../../../../components/avatar/Avatar";
import Confirm from "../../../../components/confirm/Confirm";
import DisplayUserGroup from "../../../../components/creators/user-groups/UserGroups";
import { Filters } from "../../../../components/filters/Filters";
import Users from "../../../../components/pickers/users/Users";
import Select from "../../../../components/select/Select";
import useRouting, { useParams } from "../../../../contexts/Routing";
import { useGetUserAgentsQuery, useSetUserRoleMutation, UserRole } from "../../../../__generated__/api";
import { ErrorRemote } from "../../../error/Error";
import useCurrentSession from "../../../../contexts/UserContext";

const ROLE_TO_NAME = {
    [UserRole.Administrator]: "Administrator",
    [UserRole.Agent]: "Agent",
    [UserRole.Guest]: "Guest",

};

const ROLE_OPTIONS = [
    {
        label: ROLE_TO_NAME[UserRole.Administrator],
        value: UserRole.Administrator,
    },
    {
        label: ROLE_TO_NAME[UserRole.Agent],
        value: UserRole.Agent,
    },
];

function DisplayList(): ReactElement {
    const {
        data, error, loading: isLoading, refetch,
    } = useGetUserAgentsQuery({
        fetchPolicy: "no-cache",
    });

    const [saveUserRole] = useSetUserRoleMutation();
    const [currentUsers, setCurrentUsers] = useState<string[]>();
    const [addingRole, setAddingRole] = useState<UserRole>(UserRole.Agent);

    const [filterRoles, setFilterRoles] = useState(new Set<string>());
    const [userSearch, setUserSearch] = useState<string>();
    const [deleteUser, setDeleteUser] = useState<{ id: string; name: string }>();
    const [editUser, setEditUser] = useState<{ id: string; role: UserRole }>();
    const [editRole, setEditRole] = useState<UserRole>();

    const currentSession = useCurrentSession();

    useEffect(() => {
        if (editUser) {
            setEditRole(editUser.role);
        }
    }, [editUser]);

    if (isLoading) {
        return <Loading />;
    }
    if (error) {
        return <ErrorRemote error={error} />;
    }
    let users = data?.users;
    if (filterRoles && filterRoles.size > 0) {
        users = users?.filter(user => filterRoles.has(user.role));
    }
    if (userSearch) {
        const search = userSearch.toLocaleLowerCase();
        users = users?.filter(user => user.name?.toLocaleLowerCase().includes(search));
    }

    const setUsersRole = async (ids: string[], role: UserRole) => {
        await saveUserRole({
            variables: {
                ids,
                role,
            },
        });
        refetch();
    };

    return (
        <div className="d-flex align-items-stretch flex-column">
            <Confirm
                confirmText="Delete"
                onConfirm={() => {
                    if (!deleteUser) {
                        return;
                    }
                    setUsersRole([deleteUser.id], UserRole.Guest);
                    setDeleteUser(undefined);
                }}
                shouldShow={deleteUser != null}
                onHide={() => setDeleteUser(undefined)}
            >
                <div>
                    Are you sure you want to remove all roles for user?
                    {" "}
                    <div className="mt-2 p-2 rounded alert alert-danger">
                        {deleteUser?.name}
                    </div>

                </div>
            </Confirm>

            <div className="border p-3 rounded bg-white">
                {data?.users?.isNotEmpty ? (
                    <>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="text-light mb-0">
                                Users
                            </h5>
                        </div>
                        <Filters
                            selectedCategories={filterRoles}
                            onSelected={setFilterRoles}
                            categories={[
                                {
                                    id: UserRole.Agent,
                                    name: ROLE_TO_NAME[UserRole.Agent],
                                },
                                {
                                    id: UserRole.Administrator,
                                    name: ROLE_TO_NAME[UserRole.Administrator],
                                },
                            ]}
                            search={userSearch}
                            onSetSearch={setUserSearch}
                        />
                        <div className="list-group">
                            {users?.sortBy(user => user.name?.toLowerCase()).map(user => (

                                <div className="d-flex w-100 justify-content-between list-group-item align-items-center">
                                    <div className="d-flex flex-grow-1 align-items-center">
                                        <div>
                                            <Avatar
                                                src={user.avatarUrl as string}
                                                isCircle
                                                variant="xs"
                                            />
                                            {user.name}
                                        </div>
                                        {editUser?.id === user.id && editUser ? (
                                            <>
                                                <Select
                                                    className="mx-3 col-3"
                                                    minRequired={1}
                                                    options={ROLE_OPTIONS}
                                                    value={!editRole ? undefined : {
                                                        label: ROLE_TO_NAME[editRole],
                                                        value: editRole,
                                                    }}
                                                    onChange={role => setEditRole(role.value as UserRole)}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-primary btn-sm p-2"
                                                    onClick={() => {
                                                        setUsersRole([editUser.id], editRole ?? UserRole.Guest);
                                                        setEditUser(undefined);
                                                    }}
                                                >
                                                    Save
                                                </button>
                                            </>
                                        ) : (
                                            <small className="text-light ms-3">
                                                {ROLE_TO_NAME[user.role]}
                                            </small>
                                        )}

                                    </div>

                                    {currentSession.userId !== user.id && (
                                        <div className="ms-2 text-light">
                                            <button
                                                className="btn btn-none text-gray-500 p-0 me-3"
                                                type="button"
                                                onClick={() => setEditUser({
                                                    id: user.id,
                                                    role: user.role as UserRole,
                                                })}
                                            >
                                                <AiOutlineEdit />
                                            </button>
                                            <button
                                                className="btn btn-none text-gray-500 p-0"
                                                type="button"
                                                onClick={() => setDeleteUser({
                                                    id: user.id,
                                                    name: user.name as string,
                                                })}
                                            >
                                                <BsTrash />
                                            </button>
                                        </div>
                                    )}

                                </div>

                            ))}

                        </div>
                        <div>
                            <h5 className="text-light mb-0 mt-4 mb-2">
                                Add a user
                            </h5>
                            <small className="text-light mb-1 d-block">
                                To assign a role to a user, select the users below, specify the role,
                                and click &quot;Add&quot;.
                            </small>
                            <form
                                className="d-flex justify-content-between align-items-center"
                                onSubmit={evt => {
                                    evt.preventDefault();
                                    if (!currentUsers?.isNotEmpty || !addingRole) {
                                        return;
                                    }
                                    setUsersRole(currentUsers, addingRole);
                                    setAddingRole(UserRole.Agent);
                                    setCurrentUsers([]);
                                }}
                            >
                                <div className="d-flex col align-items-center">
                                    <span className="mx-2 text-light">
                                        Users:
                                    </span>
                                    <Users
                                        minRequired={1}
                                        onSelected={user => setCurrentUsers(user)}
                                        value={currentUsers}
                                        className="w-100"
                                        isMulti
                                    />
                                </div>

                                <div className="d-flex col-3 align-items-center">
                                    <span className="mx-2 text-light">
                                        Role:
                                    </span>
                                    <Select
                                        className="w-100"
                                        minRequired={1}
                                        options={ROLE_OPTIONS}
                                        onChange={role => setAddingRole(role.value as UserRole)}
                                        value={!addingRole ? undefined : {
                                            value: addingRole,
                                            label: ROLE_TO_NAME[addingRole],
                                        }}
                                    />
                                </div>

                                <button
                                    type="submit"
                                    className="btn btn-primary py-2 col-1 ms-2"
                                >
                                    Add
                                </button>
                            </form>
                        </div>

                    </>
                ) : (
                    <div className="d-flex flex-column align-items-center justify-content-center h-100 text-light my-4">
                        <h5 className="text-light">
                            Users
                        </h5>

                        <small>
                            No users selected
                        </small>
                    </div>

                )}

            </div>

        </div>
    );
}

export default function UsersSettings(): ReactElement {
    const { Paths, historyPush } = useRouting();

    const { id } = useParams(Paths.ADMINISTRATOR_SETTING_ID);
    return id ? (
        <DisplayUserGroup
            id={id}
            canDelete
            className="border p-3 rounded mb-4 bg-white"
            onDeleted={() => {
                historyPush(Paths.ADMINISTRATOR_SETTING, {
                    setting: "user-groups",
                });
            }}
            onSave={() => {
                historyPush(Paths.ADMINISTRATOR_SETTING, {
                    setting: "user-groups",
                });
            }}
        />
    ) : <DisplayList />;
}
